import React from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Bot, X, Zap, Workflow, Brain, Shield, Eye, Sparkles, TrendingUp,
  Cog, BarChart3, Calendar, MessageSquare, Database, Cpu, Network,
  CloudLightning, Terminal, Globe, Mail, Phone, Timer, Webhook,
  MousePointer, GitBranch, ListTodo, Edit, Trash2, AlertTriangle,
  Lock, Clock, Send, Users
} from 'lucide-react';

const NewAgentDialog = ({
  open,
  onOpenChange,
  newAgentForm,
  setNewAgentForm,
  onCreateAgent
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[85vh] overflow-hidden flex flex-col p-0 [&>button]:hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Bot className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Agent Automation Builder</h2>
              <p className="text-blue-100 text-sm">Create and configure your AI agent workforce</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="text-white hover:bg-white/20">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Builder Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <Tabs defaultValue="identity" className="h-full flex flex-col">
            {/* Step Tabs */}
            <div className="border-b px-6 pt-4 bg-muted/30 sticky top-0 z-10 flex justify-center">
              <TabsList 
                className="grid grid-cols-6 w-full max-w-3xl border-2 border-blue-500/40 bg-blue-500/10 dark:bg-blue-500/20"
                style={{
                  boxShadow: '0 0 15px rgba(59, 130, 246, 0.4), 0 0 30px rgba(59, 130, 246, 0.2), inset 0 0 10px rgba(59, 130, 246, 0.1)'
                }}
              >
                <TabsTrigger value="identity" className="flex items-center gap-2 text-xs data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                  <Bot className="h-3 w-3" />
                  Identity
                </TabsTrigger>
                <TabsTrigger value="triggers" className="flex items-center gap-2 text-xs data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                  <Zap className="h-3 w-3" />
                  Triggers
                </TabsTrigger>
                <TabsTrigger value="actions" className="flex items-center gap-2 text-xs data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                  <Workflow className="h-3 w-3" />
                  Actions
                </TabsTrigger>
                <TabsTrigger value="ai-config" className="flex items-center gap-2 text-xs data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                  <Brain className="h-3 w-3" />
                  AI Config
                </TabsTrigger>
                <TabsTrigger value="permissions" className="flex items-center gap-2 text-xs data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                  <Shield className="h-3 w-3" />
                  Permissions
                </TabsTrigger>
                <TabsTrigger value="review" className="flex items-center gap-2 text-xs data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                  <Eye className="h-3 w-3" />
                  Review
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Tab Contents */}
            <div className="flex-1 overflow-y-auto p-6 min-h-0">
              {/* Identity Tab */}
              <TabsContent value="identity" className="mt-0 space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">Agent Name *</Label>
                      <Input
                        value={newAgentForm.name}
                        onChange={(e) => setNewAgentForm({ ...newAgentForm, name: e.target.value })}
                        placeholder="e.g., Sales Lead Qualifier"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label className="text-sm font-medium">Agent Type *</Label>
                      <Select 
                        value={newAgentForm.role} 
                        onValueChange={(val) => setNewAgentForm({ ...newAgentForm, role: val })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select agent type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Sales Agent">
                            <div className="flex items-center gap-2">
                              <TrendingUp className="h-4 w-4 text-green-500" />
                              Sales Agent
                            </div>
                          </SelectItem>
                          <SelectItem value="Operations Agent">
                            <div className="flex items-center gap-2">
                              <Cog className="h-4 w-4 text-blue-500" />
                              Operations Agent
                            </div>
                          </SelectItem>
                          <SelectItem value="Reporting Agent">
                            <div className="flex items-center gap-2">
                              <BarChart3 className="h-4 w-4 text-purple-500" />
                              Reporting Agent
                            </div>
                          </SelectItem>
                          <SelectItem value="Scheduling Agent">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-orange-500" />
                              Scheduling Agent
                            </div>
                          </SelectItem>
                          <SelectItem value="Communication Agent">
                            <div className="flex items-center gap-2">
                              <MessageSquare className="h-4 w-4 text-cyan-500" />
                              Communication Agent
                            </div>
                          </SelectItem>
                          <SelectItem value="Data Processing Agent">
                            <div className="flex items-center gap-2">
                              <Database className="h-4 w-4 text-red-500" />
                              Data Processing Agent
                            </div>
                          </SelectItem>
                          <SelectItem value="Custom">
                            <div className="flex items-center gap-2">
                              <Sparkles className="h-4 w-4 text-yellow-500" />
                              Custom Agent
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm font-medium">Initial Status</Label>
                      <Select 
                        value={newAgentForm.status} 
                        onValueChange={(val) => setNewAgentForm({ ...newAgentForm, status: val })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">
                            <div className="flex items-center gap-2">
                              <div className="h-2 w-2 rounded-full bg-green-500" />
                              Active - Ready to process tasks
                            </div>
                          </SelectItem>
                          <SelectItem value="paused">
                            <div className="flex items-center gap-2">
                              <div className="h-2 w-2 rounded-full bg-yellow-500" />
                              Paused - Temporarily disabled
                            </div>
                          </SelectItem>
                          <SelectItem value="training">
                            <div className="flex items-center gap-2">
                              <div className="h-2 w-2 rounded-full bg-blue-500" />
                              In Training - Learning mode
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm font-medium">Description</Label>
                      <Textarea
                        value={newAgentForm.description}
                        onChange={(e) => setNewAgentForm({ ...newAgentForm, description: e.target.value })}
                        placeholder="Describe what this agent does and its primary responsibilities..."
                        className="mt-1 min-h-[100px]"
                      />
                    </div>
                  </div>

                  {/* Right Column - Icon & Modules */}
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">Agent Icon</Label>
                      <div className="grid grid-cols-6 gap-2 mt-2">
                        {[
                          { icon: Bot, color: 'bg-blue-500' },
                          { icon: Cpu, color: 'bg-purple-500' },
                          { icon: Brain, color: 'bg-pink-500' },
                          { icon: Sparkles, color: 'bg-yellow-500' },
                          { icon: Zap, color: 'bg-orange-500' },
                          { icon: Network, color: 'bg-cyan-500' },
                          { icon: CloudLightning, color: 'bg-indigo-500' },
                          { icon: Terminal, color: 'bg-green-500' },
                          { icon: Shield, color: 'bg-red-500' },
                          { icon: Globe, color: 'bg-teal-500' },
                          { icon: Mail, color: 'bg-rose-500' },
                          { icon: Phone, color: 'bg-emerald-500' },
                        ].map(({ icon: Icon, color }, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`h-12 w-12 rounded-lg ${color} flex items-center justify-center hover:opacity-80 transition-opacity border-2 ${idx === 0 ? 'border-white ring-2 ring-blue-500' : 'border-transparent'}`}
                          >
                            <Icon className="h-6 w-6 text-white" />
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium">Assigned Modules</Label>
                      <p className="text-xs text-muted-foreground mb-2">Select which platform areas this agent can access</p>
                      <div className="space-y-2">
                        {[
                          { name: 'Sales Hub', icon: TrendingUp, desc: 'Lead management, pipeline, deals' },
                          { name: 'Operations Hub', icon: Cog, desc: 'Jobs, scheduling, workflows' },
                          { name: 'CRM / Contacts', icon: Users, desc: 'Customer data, contact records' },
                          { name: 'Automations', icon: Workflow, desc: 'Automated workflows, triggers' },
                          { name: 'Reporting', icon: BarChart3, desc: 'Analytics, reports, dashboards' },
                          { name: 'Communications', icon: MessageSquare, desc: 'Emails, notifications, messages' },
                        ].map((module) => (
                          <div 
                            key={module.name} 
                            className="flex items-center justify-between p-3 rounded-lg border-2 border-blue-500/30 hover:border-blue-500/50 hover:bg-blue-500/5 dark:hover:bg-blue-500/10 cursor-pointer transition-all duration-300"
                            style={{
                              boxShadow: '0 0 10px rgba(59, 130, 246, 0.2), 0 0 20px rgba(59, 130, 246, 0.1)'
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <module.icon className="h-4 w-4 text-blue-500" />
                              <div>
                                <p className="text-sm font-medium">{module.name}</p>
                                <p className="text-xs text-muted-foreground">{module.desc}</p>
                              </div>
                            </div>
                            <Switch />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Triggers Tab */}
              <TabsContent value="triggers" className="mt-0 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Trigger Configuration</h3>
                  <p className="text-sm text-muted-foreground mb-4">Define what events or conditions will activate this agent</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { name: 'Scheduled', icon: Timer, desc: 'Run at specific times or intervals', color: 'border-blue-500 bg-blue-500/10' },
                    { name: 'Event-Based', icon: Zap, desc: 'Triggered by system events', color: 'border-orange-500 bg-orange-500/10' },
                    { name: 'Webhook', icon: Webhook, desc: 'Triggered by external API calls', color: 'border-purple-500 bg-purple-500/10' },
                    { name: 'Manual', icon: MousePointer, desc: 'Triggered manually by users', color: 'border-green-500 bg-green-500/10' },
                    { name: 'Conditional', icon: GitBranch, desc: 'Based on data conditions', color: 'border-cyan-500 bg-cyan-500/10' },
                    { name: 'Queue-Based', icon: ListTodo, desc: 'Process items from a queue', color: 'border-red-500 bg-red-500/10' },
                  ].map((trigger) => (
                    <div key={trigger.name} className={`p-4 rounded-lg border-2 cursor-pointer hover:shadow-md transition-all ${trigger.color}`}>
                      <div className="flex items-start gap-3">
                        <trigger.icon className="h-5 w-5 mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-medium">{trigger.name}</p>
                            <Switch />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{trigger.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Schedule Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Frequency</Label>
                        <Select defaultValue="daily">
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="every-minute">Every Minute</SelectItem>
                            <SelectItem value="hourly">Hourly</SelectItem>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="custom">Custom Cron</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Time</Label>
                        <Input type="time" defaultValue="09:00" className="mt-1" />
                      </div>
                      <div>
                        <Label>Timezone</Label>
                        <Select defaultValue="est">
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="est">Eastern (EST)</SelectItem>
                            <SelectItem value="cst">Central (CST)</SelectItem>
                            <SelectItem value="pst">Pacific (PST)</SelectItem>
                            <SelectItem value="utc">UTC</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Actions Tab */}
              <TabsContent value="actions" className="mt-0 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Workflow Actions</h3>
                  <p className="text-sm text-muted-foreground mb-4">Define what this agent does when triggered</p>
                </div>

                <div className="space-y-4">
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded bg-blue-100 flex items-center justify-center">
                            <Database className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">Fetch Data</p>
                            <p className="text-xs text-muted-foreground">Query CRM for new leads</p>
                          </div>
                        </div>
                        <Badge>Step 1</Badge>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-purple-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded bg-purple-100 flex items-center justify-center">
                            <Brain className="h-4 w-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium">AI Processing</p>
                            <p className="text-xs text-muted-foreground">Analyze and qualify leads</p>
                          </div>
                        </div>
                        <Badge>Step 2</Badge>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-green-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded bg-green-100 flex items-center justify-center">
                            <Send className="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium">Take Action</p>
                            <p className="text-xs text-muted-foreground">Update records and notify team</p>
                          </div>
                        </div>
                        <Badge>Step 3</Badge>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Available Actions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        'Update CRM Record', 'Send Email', 'Send Notification',
                        'Create Task', 'Schedule Appointment', 'Generate Report',
                        'Call API', 'Send Slack Message', 'Log Activity'
                      ].map((action) => (
                        <Button key={action} variant="outline" size="sm" className="justify-start">
                          {action}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* AI Config Tab */}
              <TabsContent value="ai-config" className="mt-0 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">AI Configuration</h3>
                  <p className="text-sm text-muted-foreground mb-4">Configure the AI model and behavior settings</p>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Advanced Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-3 rounded border">
                      <div>
                        <p className="font-medium">Human-in-the-Loop</p>
                        <p className="text-xs text-muted-foreground">Require approval before executing actions</p>
                      </div>
                      <Switch />
                    </div>
                    <div className="flex items-center justify-between p-3 rounded border">
                      <div>
                        <p className="font-medium">Error Recovery</p>
                        <p className="text-xs text-muted-foreground">Auto-retry failed tasks with backoff</p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Permissions Tab */}
              <TabsContent value="permissions" className="mt-0 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Permissions & Limits</h3>
                  <p className="text-sm text-muted-foreground mb-4">Control what this agent can access and do</p>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Lock className="h-4 w-4" />
                        Data Permissions
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {[
                        { name: 'Read CRM Data', desc: 'Access contact and lead information', icon: Eye },
                        { name: 'Write CRM Data', desc: 'Create and update records', icon: Edit },
                        { name: 'Read Jobs', desc: 'Access job and project data', icon: Eye },
                        { name: 'Modify Jobs', desc: 'Update job statuses and details', icon: Edit },
                        { name: 'Access Financial Data', desc: 'View invoices and payments', icon: Eye },
                        { name: 'Delete Records', desc: 'Remove data (use with caution)', icon: Trash2 },
                      ].map((perm) => (
                        <div key={perm.name} className="flex items-center justify-between p-2 rounded hover:bg-accent">
                          <div className="flex items-center gap-2">
                            <perm.icon className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-sm font-medium">{perm.name}</p>
                              <p className="text-xs text-muted-foreground">{perm.desc}</p>
                            </div>
                          </div>
                          <Switch />
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Send className="h-4 w-4" />
                        Communication Permissions
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {[
                        { name: 'Send Internal Notifications', desc: 'Notify team members in-app', safe: true },
                        { name: 'Draft Emails', desc: 'Create email drafts for review', safe: true },
                        { name: 'Send Emails', desc: 'Send emails to contacts', safe: false },
                        { name: 'Send SMS', desc: 'Send text messages', safe: false },
                        { name: 'Post to Slack', desc: 'Send Slack messages', safe: true },
                        { name: 'Make API Calls', desc: 'Call external APIs', safe: false },
                      ].map((perm) => (
                        <div key={perm.name} className="flex items-center justify-between p-2 rounded hover:bg-accent">
                          <div className="flex items-center gap-2">
                            {!perm.safe && <AlertTriangle className="h-4 w-4 text-yellow-500" />}
                            <div>
                              <p className="text-sm font-medium">{perm.name}</p>
                              <p className="text-xs text-muted-foreground">{perm.desc}</p>
                            </div>
                          </div>
                          <Switch />
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Rate Limits & Quotas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <Label>Max Tasks/Hour</Label>
                        <Input type="number" defaultValue={50} className="mt-1" />
                      </div>
                      <div>
                        <Label>Max Concurrent</Label>
                        <Input type="number" defaultValue={5} className="mt-1" />
                      </div>
                      <div>
                        <Label>Daily Token Budget</Label>
                        <Input type="number" defaultValue={100000} className="mt-1" />
                      </div>
                      <div>
                        <Label>Timeout (seconds)</Label>
                        <Input type="number" defaultValue={30} className="mt-1" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Review Tab */}
              <TabsContent value="review" className="mt-0 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Review & Create</h3>
                  <p className="text-sm text-muted-foreground mb-4">Review your agent configuration before creating</p>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Agent Summary</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                        <div className="h-16 w-16 rounded-xl bg-blue-500 flex items-center justify-center">
                          <Bot className="h-8 w-8 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-lg">{newAgentForm.name || 'Unnamed Agent'}</h4>
                          <p className="text-sm text-muted-foreground">{newAgentForm.role || 'No role selected'}</p>
                          <Badge className="mt-1" variant={newAgentForm.status === 'active' ? 'default' : 'secondary'}>
                            {newAgentForm.status || 'active'}
                          </Badge>
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs text-muted-foreground">Description</Label>
                        <p className="text-sm">{newAgentForm.description || 'No description provided'}</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Configuration Overview</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between p-2 rounded bg-muted/50">
                        <span className="text-sm">Triggers Configured</span>
                        <Badge>2 active</Badge>
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-muted/50">
                        <span className="text-sm">Actions in Workflow</span>
                        <Badge>3 steps</Badge>
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-muted/50">
                        <span className="text-sm">AI Model</span>
                        <Badge variant="outline">GPT-4o</Badge>
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-muted/50">
                        <span className="text-sm">Modules Assigned</span>
                        <Badge>3 modules</Badge>
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-muted/50">
                        <span className="text-sm">Permissions</span>
                        <Badge variant="outline">5 granted</Badge>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card className="border-yellow-500/50 bg-yellow-500/5">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />
                      <div>
                        <p className="font-medium">Before you create this agent</p>
                        <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                          <li>• Review all permissions carefully - agents can perform actions automatically</li>
                          <li>• Consider starting in &quot;Training&quot; mode to test before going active</li>
                          <li>• Set appropriate rate limits to prevent runaway processes</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-muted/30">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <div className="flex items-center gap-3">
            <Button variant="outline">
              <Eye className="h-4 w-4 mr-2" />
              Preview Agent
            </Button>
            <Button onClick={onCreateAgent} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
              <Sparkles className="h-4 w-4 mr-2" />
              Create Agent
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewAgentDialog;
